import { isMarketDetermined, type MarketEntity } from 'queries/marketEntityTransform';

/**
 * REST polling is no longer redundant with WS for logged-in members, so there is nothing left to
 * gate it on — only a cadence to choose.
 *
 * `marketEntitiesStore` is REST-owned: live quotes and lifecycle status reach the UI through each
 * card's own `tickerQuotesStore` subscription and are overlaid locally. That leaves the poll as the
 * only thing refreshing entity data for consumers that don't overlay (event aggregates, sort/anchor
 * order, prop mainline selection), the only source of `previousPeriodPrice`/`previousDayPrice` —
 * every delta arrow's baseline, which WS has never carried — and the only way a market that opens
 * mid-session appears at all, since a ticker-filtered subscription can't surface one.
 *
 * Members read the numbers that matter off live subscriptions once those subscriptions have
 * delivered a quote; until then their displayed market keeps a fast REST fallback. Anonymous
 * visitors never poll a whole EVENT fast — that is the pre-refactor request shape, kept
 * deliberately: fast-polling the full event for every logged-out session is a real rate increase
 * on the heaviest event endpoint. Their displayed market stays fresh through the focused
 * single-market 4s poll instead (`useEventPage`'s `usePollMarketIntoStore`, which is small), and
 * everything else refreshes at the slow event cadence, exactly as before this branch.
 */
export function shouldPollEventFast(isLoggedInMember: boolean, marketsReliableOverWs = true): boolean {
    return isLoggedInMember && !marketsReliableOverWs;
}

/**
 * Whether a displayed market's card-owned WS subscription can be trusted as its live price source —
 * the `marketsReliableOverWs` input to `shouldPollEventFast` for surfaces that display one market.
 *
 * `hasLiveQuote` should be `useHasLiveMarketQuote(ticker)`: a rejected/deferred WS subscribe leaves
 * no quote in `tickerQuotesStore`, so the fast REST fallback stays on rather than silently dropping
 * to the slow cadence. (A transport outage also clears cached quotes — see `tickerQuotesStore`'s
 * `onDisconnect` — so this re-arms the fallback for the duration of an outage too.)
 *
 * Determined and closed markets count as reliable WITHOUT a quote: neither can trade, so the ticker
 * channel has nothing to deliver and waiting on a snapshot would fast-poll a frozen price for the
 * rest of the session. `status === 'closed'` deliberately, NOT `isMarketClosed` — that predicate
 * also matches paused markets (which reactivate mid-game and need the fallback the moment they do)
 * and compares `closeDate` against a non-reactive clock, so it could never re-arm.
 */
export function isChosenMarketReliableOverWs(market: MarketEntity | undefined, hasLiveQuote: boolean): boolean {
    return !!market && (isMarketDetermined(market) || market.status === 'closed' || hasLiveQuote);
}
